import { forwardRef, useId, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { Icon, type LucideIcon } from './icon';
import { Kbd } from './kbd';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Icon vorn, Suche: `icon={Search}`. */
  icon?: LucideIcon;
  /** Kürzel hinten, z. B. „⌘K“. */
  kbd?: string;
  /** `sm` in Sidebar und Filterleisten. */
  size?: 'sm' | 'md';
  /** Klassen für die Hülle (nicht das <input>). */
  className?: string;
}

/**
 * Textfeld mit optionalem Icon vorn und Kürzel hinten. Platzhalter beschreiben den
 * Inhalt („Firmenname“), ohne sichtbares Label ist `aria-label` Pflicht.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { icon, kbd, size = 'md', className, type = 'text', disabled, ...rest },
  ref,
) {
  return (
    <label
      className={cn(
        'flex w-full cursor-text items-center gap-2 rounded-md border border-input bg-muted text-foreground transition-[border-color,box-shadow] duration-150',
        'focus-within:border-ring focus-within:ring-1 focus-within:ring-ring',
        'has-[input[aria-invalid=true]]:border-danger has-[input[aria-invalid=true]]:ring-danger',
        size === 'sm' ? 'h-control-sm pl-2.5 pr-1.5' : 'h-control-md pl-3 pr-2',
        disabled && 'cursor-not-allowed opacity-45',
        className,
      )}
    >
      {icon && <Icon icon={icon} className="text-muted-foreground" />}
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        className={cn(
          'h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-inherit outline-none placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed',
          size === 'sm' ? 'text-field-sm' : 'text-field',
        )}
        {...rest}
      />
      {kbd && <Kbd>{kbd}</Kbd>}
    </label>
  );
});

/** Mehrzeiliger Text, gleiche Optik wie Input. (Ergänzung, nicht im Design System.) */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cn(
        'min-h-20 w-full rounded-md border border-input bg-muted px-3 py-2 text-field text-foreground transition-[border-color,box-shadow] duration-150 placeholder:text-muted-foreground',
        'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'aria-invalid:border-danger disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
      {...rest}
    />
  );
});

export function Label({ className, ...rest }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={cn('t-body-strong text-foreground', className)} {...rest} />;
}

export interface FieldProps {
  label: ReactNode;
  /** Kurzer Hinweis unter dem Feld. */
  hint?: ReactNode;
  /** Fehlertext; setzt aria-invalid und aria-describedby am Feld. */
  error?: ReactNode;
  required?: boolean;
  className?: string;
  /** Das Feld selbst. Bekommt id, aria-invalid und aria-describedby über die Render-Funktion. */
  children: (feld: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string; required?: boolean }) => ReactNode;
}

/** Label, Feld, Hinweis oder Fehler untereinander. (Ergänzung, nicht im Design System.) */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const beschreibung = error || hint ? `${id}-text` : undefined;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="ml-1 text-muted-foreground" aria-hidden>
            *
          </span>
        )}
      </Label>
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': beschreibung, required })}
      {error ? (
        <p id={beschreibung} className="t-small text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={beschreibung} className="t-small text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
